import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractsApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

type ContractStatus = 'active' | 'expired' | 'terminated' | 'draft' | 'renewal';

export default function ContractsList() {
  const navigate = useNavigate();
  const { t } = useTranslation(['contracts', 'common']);
  const language = useAppStore((state) => state.settings.language);
  const isRTL = language === 'ar';

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [terminateDialog, setTerminateDialog] = useState<{ open: boolean; contractId: string | null }>({
    open: false,
    contractId: null,
  });

  // Fetch contracts
  const {
    data: contracts,
    loading,
    error,
    refetch,
  } = useApi(
    () => contractsApi.getAll({ status: statusFilter !== 'all' ? statusFilter : undefined }),
    [statusFilter]
  );

  // Fetch expiring contracts
  const { data: expiringContracts } = useApi(() => contractsApi.getExpiring(), []);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  // Filter contracts by search term
  const filteredContracts = (contracts || []).filter((contract: any) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contract.property?.title?.toLowerCase().includes(searchLower) ||
      contract.tenant?.first_name?.toLowerCase().includes(searchLower) ||
      contract.tenant?.last_name?.toLowerCase().includes(searchLower) ||
      contract.contract_number?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status: ContractStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'terminated':
        return 'error';
      case 'draft':
        return 'warning';
      case 'renewal':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return format(date, 'PP', { locale: isRTL ? ar : enUS });
  };

  const formatCurrency = (amount: number) => {
    // Always use English numerals (en-SA locale)
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleTerminate = async () => {
    if (!terminateDialog.contractId) return;

    try {
      const response = await contractsApi.terminate(terminateDialog.contractId);
      if (response.error) {
        setErrorMessage(response.error);
      } else {
        setSuccessMessage(t('contractTerminated'));
        refetch();
      }
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setTerminateDialog({ open: false, contractId: null });
    }
  };

  const isExpiringSoon = (contractId: string) => {
    return expiringContracts?.some((c: any) => c.id === contractId);
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ p: 3 }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            {t('common:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/contracts/add')}
          >
            {t('addContract')}
          </Button>
        </Box>
      </Box>

      {/* Expiring Contracts Warning */}
      {expiringContracts && expiringContracts.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          {t('expiringContractsWarning', { count: expiringContracts.length })}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('status')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('common:all')}</MenuItem>
                  <MenuItem value="active">{t('statusActive')}</MenuItem>
                  <MenuItem value="expired">{t('statusExpired')}</MenuItem>
                  <MenuItem value="terminated">{t('statusTerminated')}</MenuItem>
                  <MenuItem value="draft">{t('statusDraft')}</MenuItem>
                  <MenuItem value="renewal">{t('statusRenewal')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredContracts.length} {t('contractsFound')}
      </Typography>

      {/* Contracts Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('property')}</TableCell>
                <TableCell>{t('tenant')}</TableCell>
                <TableCell>{t('startDate')}</TableCell>
                <TableCell>{t('endDate')}</TableCell>
                <TableCell>{t('rentAmount')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell>{t('document')}</TableCell>
                <TableCell align="center">{t('common:actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {t('noContracts')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredContracts.map((contract: any) => (
                  <TableRow
                    key={contract.id}
                    sx={{
                      backgroundColor: isExpiringSoon(contract.id) ? 'warning.light' : 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {contract.property?.title || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contract.property?.address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {contract.tenant
                          ? `${contract.tenant.first_name} ${contract.tenant.last_name}`
                          : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contract.tenant?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(contract.start_date)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {formatDate(contract.end_date)}
                        {isExpiringSoon(contract.id) && (
                          <Tooltip title={t('expiringSoon')}>
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatCurrency(contract.rent_amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`status${contract.status?.charAt(0).toUpperCase()}${contract.status?.slice(1)}`)}
                        color={getStatusColor(contract.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {contract.contract_document_url ? (
                        <Tooltip title={t('viewDocument')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(contract.contract_document_url, '_blank')}
                          >
                            <DocumentIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('noDocument')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title={t('common:view')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/contracts/${contract.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:edit')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/contracts/${contract.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {contract.status === 'active' && (
                          <Tooltip title={t('terminateContract')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setTerminateDialog({ open: true, contractId: contract.id })
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Terminate Confirmation Dialog */}
      <Dialog
        open={terminateDialog.open}
        onClose={() => setTerminateDialog({ open: false, contractId: null })}
      >
        <DialogTitle>{t('terminateContract')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('terminateConfirmation')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateDialog({ open: false, contractId: null })}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleTerminate} color="error" variant="contained">
            {t('common:confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
